import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const ThemeContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_THEME = {
  theme_type: 'preset',
  preset_name: 'cosmic_dark',
  custom_colors: null,
  font_heading: 'inter',
  font_body: 'inter',
};

const DEFAULT_COLORS = {
  primary: '#d4af37',
  secondary: '#9b59b6',
  accent: '#00ced1',
  background: '#0a0a1a',
  backgroundSecondary: '#1a0a2e',
  cardBg: 'rgba(26, 10, 46, 0.6)',
  text: '#f8f8ff',
  textMuted: 'rgba(248, 248, 255, 0.6)',
  border: 'rgba(212, 175, 55, 0.3)',
};

const DEFAULT_FONTS = {
  inter: { family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", import: "Inter:wght@300;400;500;600;700" },
  poppins: { family: "'Poppins', sans-serif", import: "Poppins:wght@300;400;500;600;700" },
  dm_sans: { family: "'DM Sans', sans-serif", import: "DM+Sans:wght@400;500;700" },
  space_grotesk: { family: "'Space Grotesk', sans-serif", import: "Space+Grotesk:wght@400;500;600;700" },
  plus_jakarta: { family: "'Plus Jakarta Sans', sans-serif", import: "Plus+Jakarta+Sans:wght@400;500;600;700" },
  nunito: { family: "'Nunito', sans-serif", import: "Nunito:wght@400;500;600;700" },
  cinzel: { family: "'Cinzel', serif", import: "Cinzel:wght@400;500;600;700" },
};

export function ThemeProvider({ children }) {
  const [themeSettings, setThemeSettings] = useState(DEFAULT_THEME);
  const [presetThemes, setPresetThemes] = useState({});
  const [presetFonts, setPresetFonts] = useState({});
  const [currentColors, setCurrentColors] = useState(DEFAULT_COLORS);
  const [currentFonts, setCurrentFonts] = useState({ heading: 'inter', body: 'inter' });
  const [loading, setLoading] = useState(true);

  const loadGoogleFont = useCallback((fontKey) => {
    const fontInfo = DEFAULT_FONTS[fontKey];
    if (!fontInfo) return;
    
    const linkId = `google-font-${fontKey}`;
    if (document.getElementById(linkId)) return;
    
    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fontInfo.import}&display=swap`;
    document.head.appendChild(link);
  }, []);

  const applyFonts = useCallback((headingFont, bodyFont) => {
    const root = document.documentElement;
    
    loadGoogleFont(headingFont);
    loadGoogleFont(bodyFont);
    
    const headingFamily = DEFAULT_FONTS[headingFont]?.family || DEFAULT_FONTS.inter.family;
    const bodyFamily = DEFAULT_FONTS[bodyFont]?.family || DEFAULT_FONTS.inter.family;
    
    root.style.setProperty('--font-heading', headingFamily);
    root.style.setProperty('--font-body', bodyFamily);
    
    setCurrentFonts({ heading: headingFont, body: bodyFont });
  }, [loadGoogleFont]);

  const applyThemeColors = useCallback((colors) => {
    const root = document.documentElement;
    
    root.style.setProperty('--theme-primary', colors.primary);
    root.style.setProperty('--theme-secondary', colors.secondary);
    root.style.setProperty('--theme-accent', colors.accent);
    root.style.setProperty('--theme-background', colors.background);
    root.style.setProperty('--theme-background-secondary', colors.backgroundSecondary);
    root.style.setProperty('--theme-card-bg', colors.cardBg);
    root.style.setProperty('--theme-text', colors.text);
    root.style.setProperty('--theme-text-muted', colors.textMuted);
    root.style.setProperty('--theme-border', colors.border);

    const isLight = isLightTheme(colors.background);
    root.classList.toggle('theme-light', isLight);
    root.classList.toggle('theme-dark', !isLight);
    
    setCurrentColors(colors);
  }, []);

  const isLightTheme = (bgColor) => {
    if (bgColor.startsWith('rgba')) return false;
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  };

  useEffect(() => {
    loadThemeSettings();
  }, []);

  const loadThemeSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/theme`);
      const { preset_themes, preset_fonts, ...settings } = response.data;
      
      setPresetThemes(preset_themes || {});
      setPresetFonts(preset_fonts || {});
      setThemeSettings(settings);
      
      let colors = DEFAULT_COLORS;
      if (settings.theme_type === 'preset' && preset_themes?.[settings.preset_name]) {
        colors = preset_themes[settings.preset_name].colors;
      } else if (settings.theme_type === 'custom' && settings.custom_colors) {
        colors = { ...DEFAULT_COLORS, ...settings.custom_colors };
      }
      
      applyThemeColors(colors);
      applyFonts(settings.font_heading || 'inter', settings.font_body || 'inter');
    } catch (error) {
      console.error('Failed to load theme settings:', error);
      applyThemeColors(DEFAULT_COLORS);
      applyFonts('inter', 'inter');
    } finally {
      setLoading(false);
    }
  };

  const updateTheme = async (newSettings, token) => {
    try {
      await axios.put(
        `${API}/admin/settings/theme`,
        newSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setThemeSettings(newSettings);
      
      let colors = DEFAULT_COLORS;
      if (newSettings.theme_type === 'preset' && presetThemes[newSettings.preset_name]) {
        colors = presetThemes[newSettings.preset_name].colors;
      } else if (newSettings.theme_type === 'custom' && newSettings.custom_colors) {
        colors = { ...DEFAULT_COLORS, ...newSettings.custom_colors };
      }
      
      applyThemeColors(colors);
      applyFonts(newSettings.font_heading || 'inter', newSettings.font_body || 'inter');
      return true;
    } catch (error) {
      console.error('Failed to update theme:', error);
      throw error;
    }
  };

  const previewTheme = (presetName) => {
    if (presetThemes[presetName]) {
      applyThemeColors(presetThemes[presetName].colors);
    }
  };

  const previewCustomColors = (colors) => {
    applyThemeColors({ ...DEFAULT_COLORS, ...colors });
  };

  const previewFonts = (headingFont, bodyFont) => {
    applyFonts(headingFont, bodyFont);
  };

  const resetToSaved = () => {
    let colors = DEFAULT_COLORS;
    if (themeSettings.theme_type === 'preset' && presetThemes[themeSettings.preset_name]) {
      colors = presetThemes[themeSettings.preset_name].colors;
    } else if (themeSettings.theme_type === 'custom' && themeSettings.custom_colors) {
      colors = { ...DEFAULT_COLORS, ...themeSettings.custom_colors };
    }
    applyThemeColors(colors);
    applyFonts(themeSettings.font_heading || 'inter', themeSettings.font_body || 'inter');
  };

  return (
    <ThemeContext.Provider value={{
      themeSettings,
      presetThemes,
      presetFonts,
      currentColors,
      currentFonts,
      loading,
      updateTheme,
      previewTheme,
      previewCustomColors,
      previewFonts,
      resetToSaved,
      isLightTheme: isLightTheme(currentColors.background),
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
